import type { FC } from 'react';
import { Dialog, DialogContent, Grid, Box } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';

import type { LcpnTheme } from '../../styles/theme';
import { Typography } from '../Typography';
import { Icon } from '../Icon';
import type { MapExperience } from '../NavigationBar/NavigationBarProvider';

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  paper: {
    padding: theme.spacing(6),
    maxWidth: 800,
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: theme.spacing(6),
    gap: theme.spacing(6),
    textAlign: 'left',
    border: `2px solid ${theme.colors['green-55']}`,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    width: '100%',
    '&:hover': {
      backgroundColor: theme.colors['grey-80'],
      borderColor: theme.colors['green-100'],
    },
    '@media (min-width: 900px)': {
      flexDirection: 'row',
    },
  },
  textContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    flex: 1,
    height: '100%',
  },
  description: {},
  cta: {
    color: theme.colors['green-100'],
    textDecoration: 'underline',
  },
}));

interface WelcomeModalProps {
  open: boolean;
  onSelectView: (view: MapExperience) => void;
}

export const WelcomeModal: FC<WelcomeModalProps> = ({ open, onSelectView }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: classes.paper,
      }}
    >
      <DialogContent>
        <Box mb={3} textAlign="center">
          <Typography variant="h2">{t('welcomeModal.title')}</Typography>
        </Box>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={6} display="flex">
            <Box
              className={classes.column}
              onClick={() => onSelectView('pinMap')}
            >
              <Icon variant="Map" width={36} height={36} />
              <div className={classes.textContent}>
                <Typography variant="p1" className={classes.description}>
                  {t('welcomeModal.pinMap.description')}
                </Typography>
                <Typography variant="h4" className={classes.cta}>
                  {t('welcomeModal.pinMap.cta')}
                </Typography>
              </div>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} display="flex">
            <Box
              className={classes.column}
              onClick={() => onSelectView('heatMap')}
            >
              <Icon variant="World" width={36} height={36} />
              <div className={classes.textContent}>
                <Typography variant="p1" className={classes.description}>
                  {t('welcomeModal.heatMap.description')}
                </Typography>
                <Typography variant="h4" className={classes.cta}>
                  {t('welcomeModal.heatMap.cta')}
                </Typography>
              </div>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
